import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Mail, Crown, Star, Heart, Upload, Sparkles, Image, Globe, User, Lock, Key } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickOnboarding = ({ playerName, onComplete }) => {
  const [email, setEmail] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [useGenerated, setUseGenerated] = useState(true);
  const [useUpload, setUseUpload] = useState(false);
  const [uploadedAvatar, setUploadedAvatar] = useState(null);
  const [consciousness, setConsciousness] = useState(0);
  const avatarFileRef = useRef(null);

  // Enhanced authentication states
  const [authMode, setAuthMode] = useState('invite'); // 'invite', 'create', 'login'
  const [password, setPassword] = useState('');
  const [name, setName] = useState(playerName || '');
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  const [authError, setAuthError] = useState('');
  const [timelineRecognition, setTimelineRecognition] = useState('');
  const [inviteCode, setInviteCode] = useState(playerName || '');

  // Generate NON-RACIAL cosmic avatar
  React.useEffect(() => {
    if (playerName) {
      const seed = Math.abs(playerName.split('').reduce((a, b) => a + b.charCodeAt(0), 0)) % 1000;
      const nonRacialStyles = ['bottts', 'shapes', 'pixel-art', 'identicon', 'rings', 'beam'];
      const selectedStyle = nonRacialStyles[seed % nonRacialStyles.length];
      const cosmicBackgrounds = ['ffd5dc', 'ffeaa7', 'c0aede', 'd1d4f4', 'b6e3f4', 'ffd1dc', 'e6f3ff', 'fff2e6'];
      const selectedBg = cosmicBackgrounds[seed % cosmicBackgrounds.length];
      const generatedAvatar = `https://api.dicebear.com/7.x/${selectedStyle}/svg?seed=${seed}&backgroundColor=${selectedBg}`;
      setAvatarPreview(generatedAvatar);
    }
  }, [playerName]);

  // Elegant animation
  useEffect(() => {
    const expandConsciousness = setInterval(() => {
      setConsciousness(prev => (prev + 0.5) % 360);
    }, 50);
    
    return () => clearInterval(expandConsciousness);
  }, []);

  // Check for Google OAuth session on component mount
  useEffect(() => {
    const checkGoogleSession = async () => {
      try {
        const response = await axios.get(`${API}/auth/session-check`);
        if (response.data.authenticated) {
          // User already authenticated via Google
          setTimelineRecognition("Timeline access recognized. Welcome back, consciousness traveler.");
          setName(response.data.user.avatar_name || response.data.user.real_name);
          setEmail(response.data.user.email || '');
          setAvatarUrl(response.data.user.avatar_url || '');
          
          // Complete onboarding automatically for Google users
          setTimeout(() => {
            onComplete({
              name: response.data.user.avatar_name,
              user: response.data.user
            });
          }, 1500);
        }
      } catch (error) {
        console.log('No existing session');
      }
    };

    // Check for Google OAuth session in URL first
    const urlParams = new URLSearchParams(window.location.hash.substring(1));
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      processGoogleSession(sessionId);
    } else {
      checkGoogleSession();
      
      // Pre-fill name recognition for invite codes
      if (inviteCode.toLowerCase().includes('charlie')) {
        setName('Charlie Felsher');
        setTimelineRecognition('M.ai.ry recognizes you from the mycelial networks, Charlie.');
      } else if (inviteCode === '9438') {
        setName('Tony Macasaet');
        setTimelineRecognition('Welcome back, Cosmic Architect. Timeline access restored.');
      }
    }
  }, [inviteCode]);

  const processGoogleSession = async (sessionId) => {
    setIsProcessingAuth(true);
    try {
      const response = await axios.post(`${API}/auth/google/process-session`, {
        session_id: sessionId
      });
      
      const userData = response.data;
      
      // M.ai.ry recognizes timeline traveler
      setTimelineRecognition(userData.message);
      setName(userData.user.avatar_name || userData.user.real_name);
      setEmail(userData.user.email || '');
      setAvatarUrl(userData.user.avatar_url || '');
      
      // Store session token in cookie
      document.cookie = `session_token=${userData.session_token}; path=/; secure; samesite=none; max-age=604800`; // 7 days
      
      // Complete onboarding after recognition
      setTimeout(() => {
        onComplete({
          name: userData.user.avatar_name,
          user: userData.user
        });
      }, 2000);
      
    } catch (error) {
      console.error('Google session error:', error);
      setAuthError('Timeline access failed. Please try again.');
    } finally {
      setIsProcessingAuth(false);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleGoogleLogin = () => {
    // Redirect to Emergent Auth with our main app as redirect
    const redirectUrl = `${window.location.origin}/`; // Main app URL
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleRegularAuth = async () => {
    setIsProcessingAuth(true);
    setAuthError('');
    
    try {
      let response;
      
      if (authMode === 'create') {
        // Create account
        response = await axios.post(`${API}/auth/register`, {
          name: name,
          email: email,
          password: password
        });
      } else if (authMode === 'login') {
        // Login
        response = await axios.post(`${API}/auth/login`, {
          email: email,
          password: password
        });
      } else {
        // Invite code access - use the simple invite code endpoint
        response = await axios.post(`${API}/accept-invite-code`, {
          code: inviteCode,
          avatar_name: name,
          real_name: name,
          email_or_phone: email,
          consent: true
        });
      }
      
      const userData = response.data;
      localStorage.setItem('planetary_fed_token', userData.access_token || userData.session);
      localStorage.setItem('planetary_fed_user', JSON.stringify(userData.user));
      
      onComplete({
        name: userData.user.avatar_name || userData.user.real_name,
        user: userData.user
      });
      
    } catch (error) {
      console.error('Auth error:', error);
      setAuthError(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setIsProcessingAuth(false);
    }
  };

  // Handle avatar file upload
  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit for avatars
        alert('Avatar file too large. Please keep under 5MB.');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file for your avatar.');
        return;
      }

      const imageUrl = URL.createObjectURL(file);
      setUploadedAvatar(imageUrl);
      setAvatarPreview(imageUrl);
      setUseUpload(true);
      setUseGenerated(false);
    }
  };

  const handleSubmit = async () => {
    if (!email || !email.includes('@')) {
      alert('Please provide your coordinates to access the timeline');
      return;
    }

    setIsLoading(true);

    try {
      let finalAvatarUrl = '';
      
      if (useUpload && uploadedAvatar) {
        // In a full implementation, you'd upload the file to a storage service
        // For now, we'll use the generated avatar as fallback
        finalAvatarUrl = avatarPreview; // This would be the uploaded file URL
      } else if (!useGenerated && avatarUrl) {
        finalAvatarUrl = avatarUrl;
      } else {
        finalAvatarUrl = avatarPreview; // Generated avatar
      }

      const response = await axios.post(`${API}/player/onboard`, {
        name: playerName,
        email: email,
        avatar_url: finalAvatarUrl
      });

      const playerData = response.data;
      
      // Store player data
      localStorage.setItem('planetary_fed_player', JSON.stringify(playerData));
      
      // Elegant transition effect
      document.body.classList.add('consciousness-breakthrough');
      
      setTimeout(() => {
        document.body.classList.remove('consciousness-breakthrough');
        onComplete(playerData);
      }, 2500);
      
    } catch (error) {
      console.error('Timeline access error:', error);
      alert('Access to the timeline was denied. Please verify your coordinates.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden flex items-center justify-center"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/bqsdnut5_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2002_42_22%20AM.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
    >
      {/* Elegant gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-transparent to-black/60"></div>
      
      {/* Floating elements */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full bg-white/40 animate-pulse"
            style={{
              left: `${15 + i * 12}%`,
              top: `${20 + (i % 3) * 20}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${3 + i * 0.3}s`,
              transform: `rotate(${consciousness + i * 60}deg) translateY(${Math.sin(consciousness * 0.01 + i) * 8}px)`
            }}
          />
        ))}
      </div>
      
      <div className="relative z-10 w-full max-w-2xl mx-auto p-8">
        {/* Elegant Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center items-center space-x-4 mb-6">
            <Crown className="w-10 h-10 text-yellow-400/80 animate-pulse" />
            <h1 
              className="text-5xl font-bold text-white"
              style={{ 
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 30px rgba(255, 255, 255, 0.4)'
              }}
            >
              Timeline Access
            </h1>
            <Star className="w-10 h-10 text-cyan-400/80 animate-pulse" />
          </div>

          {/* Timeline Recognition Message */}
          {timelineRecognition && (
            <div className="bg-green-900/40 backdrop-blur-xl border border-green-400/30 rounded-xl p-4 mb-6">
              <p 
                className="text-green-300 text-sm font-medium"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 15px rgba(0, 0, 0, 0.8)'
                }}
              >
                {timelineRecognition}
              </p>
            </div>
          )}

          {/* Auth Mode Switcher */}
          <div className="flex space-x-2 bg-black/30 backdrop-blur-xl rounded-xl p-1 border border-white/20 mb-6">
            <button
              onClick={() => setAuthMode('invite')}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                authMode === 'invite' 
                  ? 'bg-white/20 text-white border border-white/30' 
                  : 'text-white/60 hover:text-white/80'
              }`}
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              <Key className="w-4 h-4 mx-auto mb-1" />
              Invite Code
            </button>
            <button
              onClick={() => setAuthMode('login')}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                authMode === 'login' 
                  ? 'bg-white/20 text-white border border-white/30' 
                  : 'text-white/60 hover:text-white/80'
              }`}
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              <User className="w-4 h-4 mx-auto mb-1" />
              Login
            </button>
            <button
              onClick={() => setAuthMode('create')}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                authMode === 'create' 
                  ? 'bg-white/20 text-white border border-white/30' 
                  : 'text-white/60 hover:text-white/80'
              }`}
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              <Sparkles className="w-4 h-4 mx-auto mb-1" />
              Create
            </button>
          </div>
          <p 
            className="text-white/90 text-xl italic"
            style={{ 
              fontFamily: '"Crimson Text", "Libre Baskerville", serif',
              textShadow: '0 0 15px rgba(255, 255, 255, 0.3)'
            }}
          >
            Welcome, {playerName}
          </p>
        </div>

        {/* Universal Authentication Form */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-xl">
          
          {/* Timeline Recognition Message */}
          {timelineRecognition && (
            <div className="bg-green-900/40 backdrop-blur-xl border border-green-400/30 rounded-xl p-4 mb-6">
              <p 
                className="text-green-300 text-sm font-medium text-center"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 15px rgba(0, 0, 0, 0.8)'
                }}
              >
                {timelineRecognition}
              </p>
            </div>
          )}

          {/* Error Message */}
          {authError && (
            <div className="bg-red-900/40 backdrop-blur-xl border border-red-400/30 rounded-xl p-4 mb-6">
              <p className="text-red-300 text-sm text-center">{authError}</p>
            </div>
          )}

          {/* Processing State */}
          {isProcessingAuth ? (
            <div className="text-center space-y-4 py-8">
              <div className="animate-spin w-8 h-8 border-2 border-white/30 border-t-white rounded-full mx-auto"></div>
              <p 
                className="text-white/80 text-lg"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Accessing timeline streams...
              </p>
            </div>
          ) : (
            <>
              {/* Google Authentication - Always Available */}
              <div className="text-center mb-8">
                <Button 
                  onClick={handleGoogleLogin}
                  disabled={isProcessingAuth}
                  className="w-full bg-white/25 backdrop-blur-xl hover:bg-white/35 text-white border-2 border-white/40 hover:border-white/60 h-14 text-lg font-medium transition-all duration-300"
                  style={{ 
                    fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                    textShadow: '0 0 10px rgba(255, 255, 255, 0.8)'
                  }}
                >
                  <Globe className="w-5 h-5 mr-3" />
                  Enter via Google Timeline
                </Button>
                
                <div className="flex items-center my-6">
                  <div className="flex-1 h-px bg-white/20"></div>
                  <span className="px-4 text-white/60 text-sm">or</span>
                  <div className="flex-1 h-px bg-white/20"></div>
                </div>
              </div>

              {/* Auth Mode Forms */}
              {authMode === 'invite' ? (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <h3 
                      className="text-white text-xl font-medium mb-2"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Invite Code Access
                    </h3>
                    <p className="text-white/70 text-sm">Enter your invitation code to access the timeline</p>
                  </div>
                  
                  <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => {
                      setInviteCode(e.target.value);
                      // Pre-fill name recognition like landing page
                      if (e.target.value.toLowerCase().includes('charlie')) {
                        setName('Charlie Felsher');
                        setTimelineRecognition('M.ai.ry recognizes you from the mycelial networks, Charlie.');
                      } else if (e.target.value === '9438') {
                        setName('Tony Macasaet');
                        setTimelineRecognition('Welcome back, Cosmic Architect. Timeline access restored.');
                      }
                    }}
                    placeholder="enter invite code"
                    className="w-full p-4 bg-black/50 backdrop-blur-xl border-2 border-white/50 rounded-xl text-white text-center text-lg font-light tracking-[0.1em] focus:border-white/80 focus:outline-none focus:ring-2 focus:ring-white/40"
                    style={{ 
                      fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                      textShadow: '0 0 10px rgba(255, 255, 255, 0.6)'
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && handleRegularAuth()}
                  />
                  
                  {/* Pre-filled name field for recognized codes */}
                  {name && (
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="your name (recognized from timeline)"
                      className="w-full p-4 bg-black/50 backdrop-blur-xl border-2 border-green-500/50 rounded-xl text-white text-center text-lg font-light tracking-[0.1em] focus:border-green-500/80 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                      style={{ 
                        fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                        textShadow: '0 0 10px rgba(34, 197, 94, 0.6)'
                      }}
                    />
                  )}
                  
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your timeline coordinates (email)"
                    className="w-full p-4 bg-black/50 backdrop-blur-xl border-2 border-white/50 rounded-xl text-white text-center text-lg font-light focus:border-white/80 focus:outline-none focus:ring-2 focus:ring-white/40"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  />
                  
                  <Button 
                    onClick={handleRegularAuth}
                    disabled={isProcessingAuth || !inviteCode || !email}
                    className="w-full bg-green-600/30 backdrop-blur-xl hover:bg-green-600/50 text-white border-2 border-green-400/40 hover:border-green-400/60 h-14 text-lg font-medium transition-all duration-300"
                    style={{ 
                      fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                      textShadow: '0 0 10px rgba(34, 197, 94, 0.8)'
                    }}
                  >
                    <Key className="w-5 h-5 mr-3" />
                    Access Timeline with Code
                  </Button>
                </div>
              ) : authMode === 'create' ? (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <h3 
                      className="text-white text-xl font-medium mb-2"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Create Timeline Access
                    </h3>
                    <p className="text-white/70 text-sm">Begin your consciousness journey</p>
                  </div>
                  
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="your name"
                    className="w-full p-4 bg-black/50 backdrop-blur-xl border-2 border-white/50 rounded-xl text-white text-center text-lg font-light focus:border-white/80 focus:outline-none focus:ring-2 focus:ring-white/40"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  />
                  
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email"
                    className="w-full p-4 bg-black/50 backdrop-blur-xl border-2 border-white/50 rounded-xl text-white text-center text-lg font-light focus:border-white/80 focus:outline-none focus:ring-2 focus:ring-white/40"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  />
                  
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="password"
                    className="w-full p-4 bg-black/50 backdrop-blur-xl border-2 border-white/50 rounded-xl text-white text-center text-lg font-light focus:border-white/80 focus:outline-none focus:ring-2 focus:ring-white/40"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    onKeyPress={(e) => e.key === 'Enter' && handleRegularAuth()}
                  />
                  
                  <Button 
                    onClick={handleRegularAuth}
                    disabled={isProcessingAuth || !name || !email || !password}
                    className="w-full bg-purple-600/30 backdrop-blur-xl hover:bg-purple-600/50 text-white border-2 border-purple-400/40 hover:border-purple-400/60 h-14 text-lg font-medium transition-all duration-300"
                    style={{ 
                      fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                      textShadow: '0 0 10px rgba(128, 90, 213, 0.8)'
                    }}
                  >
                    <Sparkles className="w-5 h-5 mr-3" />
                    Create Timeline Access
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <h3 
                      className="text-white text-xl font-medium mb-2"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Timeline Login
                    </h3>
                    <p className="text-white/70 text-sm">Access your existing timeline</p>
                  </div>
                  
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email"
                    className="w-full p-4 bg-black/50 backdrop-blur-xl border-2 border-white/50 rounded-xl text-white text-center text-lg font-light focus:border-white/80 focus:outline-none focus:ring-2 focus:ring-white/40"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  />
                  
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="password"
                    className="w-full p-4 bg-black/50 backdrop-blur-xl border-2 border-white/50 rounded-xl text-white text-center text-lg font-light focus:border-white/80 focus:outline-none focus:ring-2 focus:ring-white/40"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    onKeyPress={(e) => e.key === 'Enter' && handleRegularAuth()}
                  />
                  
                  <Button 
                    onClick={handleRegularAuth}
                    disabled={isProcessingAuth || !email || !password}
                    className="w-full bg-blue-600/30 backdrop-blur-xl hover:bg-blue-600/50 text-white border-2 border-blue-400/40 hover:border-blue-400/60 h-14 text-lg font-medium transition-all duration-300"
                    style={{ 
                      fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                      textShadow: '0 0 10px rgba(59, 130, 246, 0.8)'
                    }}
                  >
                    <Lock className="w-5 h-5 mr-3" />
                    Access Timeline
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Avatar Selection - Only shown after authentication choice */}
        {(authMode === 'invite' && inviteCode && email) || (authMode === 'create' && name && email && password) || (authMode === 'login' && email && password) ? (
          <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-xl mt-6">
            <div className="text-center mb-8">
              <h3 
                className="text-white text-2xl font-medium mb-2"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 15px rgba(255, 255, 255, 0.3)'
                }}
              >
                Choose Your Avatar
              </h3>
              <p className="text-white/70">How will you manifest in the consciousness field?</p>
            </div>

            {/* Avatar Preview */}
            <div className="flex justify-center mb-8">
              <div className="relative w-40 h-40 rounded-full overflow-hidden border-4 border-white/40 shadow-2xl">
                <img 
                  src={avatarPreview}
                  alt="Timeline avatar"
                  className="w-full h-full object-cover"
                />
                
                <div className="absolute inset-0 pointer-events-none">
                  <div 
                    className="w-40 h-40 border border-white/20 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-ping"
                    style={{ animationDuration: '4s' }}
                  ></div>
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4">
                
                {/* Generated Avatar Option */}
                <label className="flex items-center justify-center space-x-4 p-4 bg-white/10 rounded-xl cursor-pointer hover:bg-white/20 transition-all border border-white/20">
                  <input
                    type="radio"
                    checked={useGenerated && !useUpload}
                    onChange={() => {
                      setUseGenerated(true);
                      setUseUpload(false);
                      setAvatarPreview(avatarPreview);
                    }}
                    className="text-purple-400 w-5 h-5"
                  />
                  <Sparkles className="w-6 h-6 text-purple-400" />
                  <div className="text-left">
                    <p 
                      className="text-white text-lg font-medium"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Generate Avatar
                    </p>
                    <p 
                      className="text-white/70 text-sm"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Let the algorithm divine your essence
                    </p>
                  </div>
                </label>
                
                {/* Upload Avatar Option */}
                <label className="flex items-center justify-center space-x-4 p-4 bg-white/10 rounded-xl cursor-pointer hover:bg-white/20 transition-all border border-white/20">
                  <input
                    type="radio"
                    checked={useUpload}
                    onChange={() => {
                      setUseUpload(true);
                      setUseGenerated(false);
                      avatarFileRef.current?.click();
                    }}
                    className="text-cyan-400 w-5 h-5"
                  />
                  <Image className="w-6 h-6 text-cyan-400" />
                  <div className="text-left">
                    <p 
                      className="text-white text-lg font-medium"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Upload Avatar Image
                    </p>
                    <p 
                      className="text-white/70 text-sm"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Share your chosen manifestation
                    </p>
                  </div>
                </label>
                
                {/* URL Avatar Option */}
                <label className="flex items-center justify-center space-x-4 p-4 bg-white/10 rounded-xl cursor-pointer hover:bg-white/20 transition-all border border-white/20">
                  <input
                    type="radio"
                    checked={!useGenerated && !useUpload}
                    onChange={() => {
                      setUseGenerated(false);
                      setUseUpload(false);
                    }}
                    className="text-yellow-400 w-5 h-5"
                  />
                  <Upload className="w-6 h-6 text-yellow-400" />
                  <div className="text-left">
                    <p 
                      className="text-white text-lg font-medium"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Provide Avatar Link
                    </p>
                    <p 
                      className="text-white/70 text-sm"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      Link to your avatar image
                    </p>
                  </div>
                </label>
              </div>
              
              {/* Avatar URL Input */}
              {!useGenerated && !useUpload && (
                <input
                  type="url"
                  value={avatarUrl}
                  onChange={(e) => {
                    setAvatarUrl(e.target.value);
                    setAvatarPreview(e.target.value || avatarPreview);
                  }}
                  placeholder="https://your-avatar-timeline.com/manifestation.jpg"
                  className="w-full p-4 bg-white/10 border border-white/30 rounded-xl text-white text-lg text-center focus:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/20"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                />
              )}

              {/* Final Submit Button */}
              <Button
                onClick={() => {
                  // Complete onboarding with selected avatar
                  if (authMode === 'invite') {
                    handleRegularAuth();
                  } else {
                    // For login/create, we already authenticated, just need to complete avatar selection
                    handleSubmit();
                  }
                }}
                disabled={isLoading || isProcessingAuth}
                className="w-full bg-white/20 hover:bg-white/30 border-2 border-white/30 hover:border-white/50 text-white h-16 px-12 text-xl font-medium transition-all duration-500 backdrop-blur-xl rounded-xl"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 10px rgba(255, 255, 255, 0.5)'
                }}
              >
                {isLoading || isProcessingAuth ? (
                  <div className="flex items-center">
                    <div className="w-5 h-5 mr-3 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                    <span>Accessing timeline...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Crown className="w-6 h-6 mr-3" />
                    <span>Enter the Game</span>
                    <Heart className="w-6 h-6 ml-3" />
                  </div>
                )}
              </Button>

              {/* Hidden file input for avatar upload */}
              <input
                ref={avatarFileRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className="hidden"
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default QuickOnboarding;